import React, { useState } from 'react';
import ConfirmModal from '../ConfirmModal';
import { useNavigate } from 'react-router-dom';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import {
  Gamepad2, Radio, Clock, CheckCircle, Trophy,
  Calendar, Users, Loader, ChevronDown, ChevronUp, Trash2
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';

/**
 * Reusable GamesList component for displaying a list of games
 *
 * Props:
 * - games: Array of game objects
 * - loading: Boolean indicating loading state
 * - error: Error message string (optional)
 * - onRetry: Function to call when retry button is clicked
 * - emptyTitle: Custom title for empty state (default: "No games yet")
 * - emptyMessage: Custom message for empty state
 * - showHeader: Boolean to show/hide the header (default: false)
 * - headerTitle: Custom header title
 * - pagination: { total, hasMore } object for pagination info
 * - onLoadMore: Function to call when "Load More" is clicked
 * - compact: Boolean for compact mode (less padding, smaller text)
 * - onGameClick: Optional callback when game is clicked (overrides default navigation)
 */
const GamesList = ({
  games = [],
  loading = false,
  error = null,
  onRetry,
  emptyTitle = "No games yet",
  emptyMessage = "Join events and participate in games to see your game history here.",
  showHeader = false,
  headerTitle = "Games",
  pagination = { total: 0, hasMore: false },
  onLoadMore,
  compact = false,
  onGameClick,
  onDeleteGame,
  currentUserId,
  isAdmin = false
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, gameId: null });

  // Handle game click - use custom handler if provided, otherwise navigate
  const handleGameClick = (game) => {
    if (onGameClick) {
      onGameClick(game);
      return;
    }

    const eventId = game.id;
    const gameId = game.game?.id;

    if (game.ownerType === 'group' && game.ownerId && gameId) {
      // Open a dynamic game-view tab on the squad page for past group games
      navigate(`/squads/${game.ownerId}?tab=game-view&eventId=${eventId}&gameId=${gameId}`);
    } else if (game.ownerType === 'tournament' && game.game?.liveLink) {
      // liveLink carries roundId, which TournamentDetailPage's own effect
      // reads to auto-open the correct live tab on load. A full navigation
      // (not React Router's client-side navigate) is deliberate here: React
      // Router reuses the same TournamentDetailPage instance across
      // different tournament ids (same route pattern, just a different
      // :id) rather than remounting it, so a client-side nav would carry
      // over whatever live-game tabs were already open from a different
      // tournament — confirmed bug: jumping here from one tournament's live
      // game bled that tournament's tab into the new one. A real page load
      // guarantees TournamentDetailPage mounts with a clean slate, and its
      // own roundId effect opens exactly the one relevant tab.
      window.location.href = game.game.liveLink;
    } else if (game.ownerType === 'tournament') {
      navigate(`/tournament/${game.ownerId}`);
    } else if (game.game?.liveLink) {
      navigate(game.game.liveLink);
    } else {
      navigate(`/events/${eventId}`);
    }
  };

  // Convert UTC date and time to local timezone for display
  const formatLocalDate = (utcDate) => {
    if (!utcDate) return '';
    try {
      const [year, month, day] = utcDate.split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day));
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return utcDate;
    }
  };

  // Generate a deterministic avatar background color from a name string
  const getAvatarColor = (name) => {
    const palette = [
      'bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-rose-500',
      'bg-amber-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
      'bg-cyan-500', 'bg-orange-500',
    ];
    if (!name) return palette[0];
    const hash = name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return palette[hash % palette.length];
  };

  // Get game status display info
  const getGameStatusDisplay = (game) => {
    const status = game.game?.status || 'unknown';

    switch (status) {
      case 'active':
        return {
          icon: <Radio className="w-3.5 h-3.5 text-red-500 animate-pulse" />,
          label: 'Active',
          className: 'bg-red-100 text-red-800 border-red-200'
        };
      case 'finished':
        return {
          icon: <Clock className="w-3.5 h-3.5 text-yellow-600" />,
          label: 'Finished',
          className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
        };
      case 'completed':
        return {
          icon: <CheckCircle className="w-3.5 h-3.5 text-green-600" />,
          label: 'Completed',
          className: 'bg-green-100 text-green-800 border-green-200'
        };
      default:
        return {
          icon: <Trophy className="w-3.5 h-3.5 text-gray-600" />,
          label: status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          className: 'bg-gray-100 text-gray-800 border-gray-200'
        };
    }
  };

  // Determine if team A is the winner
  const isTeamAWinner = (game) => {
    if (!game.game) return false;
    const winner = game.game.winner;
    if (winner === 'A' || winner === 'a' || winner === 'teamA' || winner === 'participant_a') return true;
    // Fallback: compare scores
    const scoreA = game.game.participantAScore;
    const scoreB = game.game.participantBScore;
    if (scoreA != null && scoreB != null && scoreA > scoreB) return true;
    return false;
  };

  // Determine if team B is the winner
  const isTeamBWinner = (game) => {
    if (!game.game) return false;
    const winner = game.game.winner;
    if (winner === 'B' || winner === 'b' || winner === 'teamB' || winner === 'participant_b') return true;
    // Fallback: compare scores
    const scoreA = game.game.participantAScore;
    const scoreB = game.game.participantBScore;
    if (scoreA != null && scoreB != null && scoreB > scoreA) return true;
    return false;
  };

  // Game Card Component
  const GameCard = ({ game }) => {
    const [showMatchBreakdown, setShowMatchBreakdown] = useState(false);
    const gameStatusDisplay = getGameStatusDisplay(game);
    const teamAName = game.game?.participantAInfo?.name;
    const teamBName = game.game?.participantBInfo?.name;

    // Parse per-match breakdown from score_summary JSON
    const scoreSummary = game.game?.scoreSummary;
    const matchBreakdown = (() => {
      if (!scoreSummary || typeof scoreSummary !== 'object') return null;
      const matches = scoreSummary.matches;
      if (!Array.isArray(matches) || matches.length === 0) return null;
      return matches;
    })();

    // For display: if exactly 1 match in score_summary, use its scores (actual points);
    // otherwise fall back to game-level scores (sets/games won)
    const singleMatch = matchBreakdown?.length === 1 ? matchBreakdown[0] : null;
    const scoreA = singleMatch ? singleMatch.participant_a_score : game.game?.participantAScore;
    const scoreB = singleMatch ? singleMatch.participant_b_score : game.game?.participantBScore;
    const oversA = singleMatch?.participant_a_overs ?? null;
    const oversB = singleMatch?.participant_b_overs ?? null;
    const hasTeamScores = teamAName && teamBName && scoreA != null && scoreB != null;
    const teamAWins = isTeamAWinner(game);
    const teamBWins = isTeamBWinner(game);

    const avatarSize = compact ? 'w-10 h-10 text-base' : 'w-14 h-14 text-xl';
    const scoreSize = compact ? 'text-2xl' : 'text-3xl';

    // Participant avatar: colored circle with first initial
    const ParticipantAvatar = ({ name, imgUrl }) => {
      const initial = name ? name.charAt(0).toUpperCase() : '?';
      const colorClass = getAvatarColor(name);
      const baseClass = `${avatarSize} rounded-full ring-2 ring-white dark:ring-gray-800`;

      return (
        <div className="relative">
          {imgUrl && (
            <img
              src={resolveImageUrl(imgUrl)}
              alt={name}
              className={`${baseClass} object-cover`}
              onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }}
            />
          )}
          <div
            className={`${baseClass} ${colorClass} items-center justify-center text-white font-bold`}
            style={{ display: imgUrl ? 'none' : 'flex' }}
          >
            {initial}
          </div>
        </div>
      );
    };

    const isOwner = currentUserId != null && String(game.createdBy) === String(currentUserId);

    const handleDeleteClick = (e) => {
      e.stopPropagation();
      setDeleteConfirm({ isOpen: true, gameId: game.game?.id });
    };

    return (
      <div
        onClick={() => handleGameClick(game)}
        className={`${theme.card} rounded-xl ${compact ? 'p-3' : 'p-4'} hover:shadow-lg transition-all duration-300 border ${theme.border} cursor-pointer flex flex-col h-full`}
      >
        {/* Game Name + Status */}
        <div className="flex items-center justify-between mb-3">
          <span className={`font-semibold ${compact ? 'text-sm' : 'text-base'} ${theme.text} truncate`}>
            {game.game?.gameName || game.subject || 'Unnamed Game'}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            {game.game?.status && (
              <span className={`inline-flex items-center gap-x-1 px-2 py-0.5 rounded-full text-xs font-medium border ${gameStatusDisplay.className}`}>
                {gameStatusDisplay.icon}
                <span>{gameStatusDisplay.label}</span>
              </span>
            )}
            {(isOwner || isAdmin) && onDeleteGame && (
              <button
                onClick={handleDeleteClick}
                title="Delete game"
                className="p-1 rounded-full text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Scoreboard: Logo / Score / Name for each participant */}
        {hasTeamScores ? (
          <div className={`${theme.backgroundSecondary} rounded-xl ${compact ? 'p-3' : 'p-4'} mb-3 flex items-center justify-between`}>
            {/* Team A */}
            <div className="flex flex-col items-center flex-1 gap-1">
              <ParticipantAvatar
                name={teamAName}
                imgUrl={game.game?.participantAInfo?.avatarUrl}
              />
              <span className={`${scoreSize} font-bold tabular-nums leading-none mt-1 ${teamAWins ? 'text-green-500' : theme.text}`}>
                {scoreA}
              </span>
              {oversA && <span className={`text-xs ${theme.textMuted}`}>({oversA})</span>}
              <span className={`text-xs ${theme.textMuted} text-center leading-tight max-w-[80px] truncate`}>
                {teamAName}
              </span>
              {teamAWins && <span className="text-xs font-semibold text-green-500">Winner</span>}
            </div>

            {/* VS divider */}
            <div className="flex flex-col items-center px-2">
              <span className={`text-xs font-bold ${theme.textMuted} tracking-widest`}>VS</span>
            </div>

            {/* Team B */}
            <div className="flex flex-col items-center flex-1 gap-1">
              <ParticipantAvatar
                name={teamBName}
                imgUrl={game.game?.participantBInfo?.avatarUrl}
              />
              <span className={`${scoreSize} font-bold tabular-nums leading-none mt-1 ${teamBWins ? 'text-green-500' : theme.text}`}>
                {scoreB}
              </span>
              {oversB && <span className={`text-xs ${theme.textMuted}`}>({oversB})</span>}
              <span className={`text-xs ${theme.textMuted} text-center leading-tight max-w-[80px] truncate`}>
                {teamBName}
              </span>
              {teamBWins && <span className="text-xs font-semibold text-green-500">Winner</span>}
            </div>
          </div>
        ) : null}

        {/* Per-match expandable — only when there are 2+ matches */}
        {matchBreakdown && matchBreakdown.length > 1 && (
          <div className="mb-3">
            <button
              onClick={(e) => { e.stopPropagation(); setShowMatchBreakdown(v => !v); }}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg ${theme.backgroundSecondary} ${theme.cardHover} transition-colors`}
            >
              <span className={`text-xs font-medium ${theme.textMuted}`}>
                Matches ({matchBreakdown.length})
              </span>
              {showMatchBreakdown
                ? <ChevronUp className={`w-3.5 h-3.5 ${theme.textMuted}`} />
                : <ChevronDown className={`w-3.5 h-3.5 ${theme.textMuted}`} />
              }
            </button>

            {showMatchBreakdown && (
              <div className={`mt-1 rounded-lg overflow-hidden border ${theme.border}`}>
                <div className={`grid grid-cols-3 px-3 py-1.5 ${theme.backgroundSecondary}`}>
                  <span className={`text-xs font-semibold ${theme.textMuted}`}>Match</span>
                  <span className={`text-xs font-semibold text-center ${theme.textMuted} truncate`}>{teamAName || 'A'}</span>
                  <span className={`text-xs font-semibold text-center ${theme.textMuted} truncate`}>{teamBName || 'B'}</span>
                </div>
                {matchBreakdown.map((m, i) => {
                  const aWon = m.participant_a_score > m.participant_b_score;
                  const bWon = m.participant_b_score > m.participant_a_score;
                  return (
                    <div key={i} className={`grid grid-cols-3 px-3 py-2 border-t ${theme.border}`}>
                      <span className={`text-xs ${theme.textMuted}`}>#{m.match_number ?? i + 1}</span>
                      <span className={`text-xs font-bold text-center tabular-nums ${aWon ? 'text-green-500' : theme.text}`}>
                        {m.participant_a_score ?? '-'}
                      </span>
                      <span className={`text-xs font-bold text-center tabular-nums ${bWon ? 'text-green-500' : theme.text}`}>
                        {m.participant_b_score ?? '-'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Game Info */}
        <div className={`flex flex-wrap items-center gap-2 text-xs mt-auto`}>
          <div className={`flex items-center gap-x-1 ${theme.textMuted}`}>
            <Calendar className="w-3.5 h-3.5" />
            <span>{formatLocalDate(game.date)}</span>
          </div>
          {game.participantCount > 0 && (
            <div className={`flex items-center gap-x-1 ${theme.textMuted}`}>
              <Users className="w-3.5 h-3.5" />
              <span>{game.participantCount}</span>
            </div>
          )}
          {game.game?.sportName && (
            <div className={`flex items-center gap-x-1 ${theme.textMuted}`}>
              <Gamepad2 className="w-3.5 h-3.5" />
              <span>{game.game.sportName}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Loading state
  if (loading && games.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center ${compact ? 'py-8' : 'py-16'}`}>
        <Loader className={`w-8 h-8 animate-spin ${theme.textMuted} mb-4`} />
        <p className={`text-sm ${theme.textMuted}`}>Loading games...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`text-center ${compact ? 'py-8' : 'py-16'}`}>
        <div className="p-4 bg-red-50 rounded-lg inline-block mb-4">
          <Gamepad2 className="w-12 h-12 text-red-400" />
        </div>
        <p className="text-red-600 mb-4">{error}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className={`${btn.base} ${btn.md} ${theme.buttonPrimary}`}
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  // Empty state
  if (games.length === 0) {
    return (
      <div className={`text-center ${compact ? 'py-8' : 'py-16'}`}>
        <div className={`p-4 ${theme.backgroundSecondary} rounded-full inline-block mb-4`}>
          <Gamepad2 className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} ${theme.textMuted}`} />
        </div>
        <h3 className={`${compact ? 'text-lg' : 'text-xl'} font-semibold ${theme.text} mb-2`}>{emptyTitle}</h3>
        <p className={`${theme.textMuted} ${compact ? 'text-sm' : ''} max-w-md mx-auto`}>
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Optional Header */}
      {showHeader && (
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${theme.text}`}>{headerTitle}</h3>
          {pagination.total > 0 && (
            <span className={`text-sm ${theme.textMuted}`}>
              {pagination.total} game{pagination.total !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {/* Games Grid */}
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ${compact ? 'gap-3' : 'gap-4'}`}>
        {games.map((game) => (
          <GameCard key={`${game.id}-${game.game?.id}`} game={game} />
        ))}
      </div>

      {/* Load More */}
      {pagination.hasMore && onLoadMore && (
        <div className="text-center mt-6">
          <button
            onClick={onLoadMore}
            disabled={loading}
            className={`${btn.base} ${btn.lg} ${theme.buttonSecondary}`}
          >
            {loading ? (
              <span className="flex items-center gap-x-2">
                <Loader className="w-4 h-4 animate-spin" />
                <span>Loading...</span>
              </span>
            ) : (
              'Load More Games'
            )}
          </button>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, gameId: null })}
        onConfirm={() => {
          onDeleteGame(deleteConfirm.gameId);
          setDeleteConfirm({ isOpen: false, gameId: null });
        }}
        title="Delete Game"
        message="Delete this game? This cannot be undone."
        confirmText="Delete"
        confirmStyle="danger"
      />
    </div>
  );
};

export default GamesList;
